import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NumberFormatterService } from '../../../../../core/services/number.formatter.service';
import { AppTextMask } from '../../../../../core/models/interface/app-text-mask';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { User } from 'anchor-shared/lib/model/entity/user';
import { UserDAO } from '../../../../../core/services/dao/userDAO';
import { Subscription } from 'rxjs';
import { PaymentDAO } from '../../../../../core/services/dao/paymentDAO';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { CharterDAO } from '../../../../../core/services/dao/charterDAO';
import { PayoutDAO } from '../../../../../core/services/dao/payoutDAO';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { IndividualCost } from '../../../../../core/models/interface/additional-cost';

@Component({
  selector: 'app-create-payout-from-payment-dialog',
  templateUrl: './create-payout-from-payment-dialog.component.html',
  styleUrls: ['./create-payout-from-payment-dialog.component.scss'],
})
export class CreatePayoutFromPaymentDialogComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  filteredUsers: Observable<User[]>;
  currencyMask: AppTextMask;
  percentageMask: AppTextMask;
  payment: CreatablePayment;
  payee: any;
  charter: Charter;
  costTotal: string;
  userOptions: any;
  manualUserForm: FormGroup;
  costForm: FormGroup;
  autoControl: FormControl = new FormControl();
  subs: Subscription[] = [];
  payeePay: string;
  payeeId: number;
  payeeName: string;
  payeePaypalEmail: string;
  payeeEmail: string;
  manualPaymentType: string;
  companyName: string;
  paymentNetwork: string;
  costDescription: string;
  description: string;
  payoutLoading: boolean;

  constructor(
    private dialogRef: MatDialogRef<CreatePayoutFromPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      payment: CreatablePayment;
      charter: Charter;
    },
    private numberFormatterService: NumberFormatterService,
    private paymentDAO: PaymentDAO,
    private charterDAO: CharterDAO,
    private payoutDAO: PayoutDAO,
    private userDAO: UserDAO,
    private matSnackBar: MatSnackBar,
  ) {
    this.currencyMask = this.numberFormatterService.createPriceMask();
    this.percentageMask = this.numberFormatterService.createPercentageMask();
    this.payment = this.data.payment;
    this.charter = this.data.charter;
    this.payeePay = '0.00';
    this.costTotal = this.payment.amount;
    this.costDescription = this.payment.name;
    this.payoutLoading = false;

    this.costForm = new FormGroup({
      payeePay: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
      ]),
      payee: new FormControl('', [Validators.required]),
    });
    this.manualUserForm = new FormGroup({
      payeeName: new FormControl('', [Validators.required]),
      payeeEmail: new FormControl('', [Validators.required, Validators.email]),
      companyName: new FormControl('', []),
      paymentNetwork: new FormControl('', [Validators.required]),
      payeePaypalEmail: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    const userSub = this.userDAO.getUsersForPayouts().subscribe((data) => {
      this.userOptions = data;
      this.filteredUsers = this.autoControl.valueChanges.pipe(
        startWith(''),
        map((val) => (val && val.length >= 4 ? this._filter(val) : [])),
      );
    });
    this.subs.push(userSub);
  }

  selectPayee(event, payee: string): void {
    this.payee = payee;
    if (this.payee === 'Boat Owner') {
      let boatOwnerUser = this.charter.boat.owner.user;
      this.payeePay = '0.00';
      this.payeeId = boatOwnerUser.id;
      this.payeeName = boatOwnerUser.firstName + ' ' + boatOwnerUser.lastName;
      this.payeePaypalEmail = boatOwnerUser.paypalEmail;
      this.manualPaymentType = 'owner';
    } else if (this.payee === 'Captain') {
      if (this.charter.captain) {
        let captainUser = this.charter.captain.user;
        this.payeePay = '0.00';
        this.payeeId = captainUser.id;
        this.payeeName = captainUser.firstName + ' ' + captainUser.lastName;
        this.payeePaypalEmail = captainUser.paypalEmail;
      } else {
        this.payeeName = 'no captain assigned';
      }
      this.manualPaymentType = 'captain';
    } else if (this.payee === 'Anchor') {
      this.payeeName = 'Anchor';
      this.payeePay = this.payment.amount;
    }
  }

  selectUser(event, user: User) {
    if (event.isUserInput) {
      this.payeeId = user.id;
      this.payeePaypalEmail = user.paypalEmail;
      this.payeeName = user.firstName + ' ' + user.lastName;
    }
  }

  _filter(val: string): User[] {
    return this.userOptions.filter((s) =>
      new RegExp(val, 'gi').test(s.firstName + ' ' + s.lastName),
    );
  }

  // Save info as a Payout and push it to the database.
  savePayoutCreation(): void {
    let additionalCosts;
    this.payoutLoading = true;

    if (this.charter.additionalCosts) {
      typeof this.charter.additionalCosts === 'object'
        ? (additionalCosts = this.charter.additionalCosts)
        : (additionalCosts = this.numberFormatterService.parseArrayFromJson(
            this.charter.additionalCosts,
          ));
    } else {
      additionalCosts = {
        fuelCosts: {
          gallons: null,
          cost: '0.00',
          costPerGal: null,
        },
        additionalCharges: {
          cost: '0.00',
          individualCosts: [],
        },
        total: '0.00',
      };
    }

    this.payeePay = this.numberFormatterService
      .unMaskPrice(this.payeePay)
      .toFixed(2)
      .toString();

    this.costTotal = this.numberFormatterService
      .unMaskPrice(this.costTotal)
      .toFixed(2)
      .toString();

    this.setCreatedDateForIndividualCost(additionalCosts);

    let newPayout: Partial<Payout>;
    newPayout = {
      basePay: this.payeePay,
      actualPay: this.payeePay,
      payee: this.payeeName,
      payeePaypalHandle: this.payeePaypalEmail,
      type: 'manual',
      manualPaymentType: this.manualPaymentType,
      userId: this.payeeId,
      name: this.payment.name,
      charterId: this.charter.id,
      paid: false,
      notes: this.payment.notes,
      // @ts-ignore
      charterDate: this.charter.date,
    };

    const payoutCreationSub = this.payoutDAO
      .createMultiple([newPayout])
      .subscribe((createdPayout) => {
        additionalCosts.additionalCharges.cost = (
          parseFloat(additionalCosts.additionalCharges.cost) +
          parseFloat(this.costTotal)
        )
          .toFixed(2)
          .toString();
        const charterSub = this.charterDAO
          .updateFromPartial(this.charter.id, {
            additionalCosts: additionalCosts,
          })
          .subscribe((updated) => {
            let responseObj = {
              additionalCost: additionalCosts,
              newPayout: createdPayout[0],
            };
            this.charter.payouts.push(createdPayout[0]);
            this.dialogRef.close(responseObj);
            this.payoutLoading = false;
            this.openSnackBar('Payout Successfully Created');
          });
        this.subs.push(charterSub);
      });
    this.subs.push(payoutCreationSub);
  }

  openSnackBar(message: string) {
    this.matSnackBar.open(message, null, {
      duration: 2500,
      verticalPosition: 'top',
    });
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  compareValues(payment: string, payoutPrice: string): boolean {
    const numPayment = parseFloat(payment);
    const numPayoutPrice = parseFloat(payoutPrice);

    if (numPayoutPrice > numPayment) {
      return true;
    }
    return false;
  }

  positiveNumberOnly(event): Boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode === 45) {
      return false;
    }
    return true;
  }

  private setCreatedDateForIndividualCost(additionalCosts: any): void {
    if (this.payment.costUUID) {
      additionalCosts.additionalCharges.individualCosts.forEach(
        (cost: IndividualCost) => {
          if (cost.uuid === this.payment.costUUID) {
            cost.payoutCreatedDate = moment().utc().format();
          }
        },
      );
    }
  }
}

export interface CreatablePayment {
  name: string;
  amount: string;
  id?: number;
  costUUID?: string;
  notes?: string;
}
